import { createContext, useContext, useCallback, ReactNode } from 'react';
import { useAuth, UserRole } from './AuthContext';

// Permission actions (mirrors CanCan)
export type PermissionAction =
  | 'read'
  | 'create'
  | 'update'
  | 'delete'
  | 'manage'
  | 'index'
  | 'show'
  | 'edit'
  | 'destroy'
  | 'assign'
  | 'transfer'
  | 'convert'
  | 'deactivate'
  | 'activate';

// Permission subjects (entities)
export type PermissionSubject =
  | 'User'
  | 'Client'
  | 'Lead'
  | 'Contact'
  | 'Tutor'
  | 'Student'
  | 'Assignment'
  | 'DesiredPlacement'
  | 'TutoringSession'
  | 'OnlineSession'
  | 'Todo'
  | 'Note'
  | 'Report'
  | 'Dashboard'
  | 'CatalogItem'
  | 'all';

// Role hierarchy
const ADMIN_ROLES: UserRole[] = ['admin', 'developer_admin'];
const EXECUTIVE_ROLES: UserRole[] = ['executive'];
const DIRECTOR_ROLES: UserRole[] = [
  'regional_director',
  'client_director',
  'lead_director',
  'tutor_director',
  'service_director',
  'product_director',
];
const SUPPORT_ROLES: UserRole[] = ['sales_rep', 'support_rep', 'tutor_support'];
const CATALOG_ROLES: UserRole[] = ['developer_admin', 'executive', 'course_operations'];

interface PermissionContextValue {
  can: (action: PermissionAction, subject: PermissionSubject, instance?: unknown) => boolean;
  cannot: (action: PermissionAction, subject: PermissionSubject, instance?: unknown) => boolean;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  isAdmin: boolean;
  isDirector: boolean;
  isSupport: boolean;
}

const PermissionContext = createContext<PermissionContextValue | undefined>(undefined);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userRoles = user?.roles || [];

  const hasRole = useCallback(
    (role: UserRole) => userRoles.includes(role),
    [userRoles]
  );

  const hasAnyRole = useCallback(
    (roles: UserRole[]) => roles.some((role) => userRoles.includes(role)),
    [userRoles]
  );

  const isAdmin = hasAnyRole(ADMIN_ROLES);
  const isExecutive = hasAnyRole(EXECUTIVE_ROLES);
  const isDirector = hasAnyRole(DIRECTOR_ROLES);
  const isSupport = hasAnyRole(SUPPORT_ROLES);

  const can = useCallback(
    (action: PermissionAction, subject: PermissionSubject, _instance?: unknown) => {
      // Admins can do anything
      if (isAdmin) return true;

      // Check specific permissions based on role hierarchy
      switch (subject) {
        case 'all':
          return isAdmin;

        case 'User':
          if (action === 'read' || action === 'index' || action === 'show') {
            return true; // Most users can view other users
          }
          if (action === 'manage' || action === 'create' || action === 'update' || action === 'delete') {
            return isAdmin;
          }
          return false;

        case 'Client':
        case 'Lead':
        case 'Contact':
          if (action === 'read' || action === 'index' || action === 'show') {
            return isAdmin || isExecutive || isDirector || isSupport;
          }
          if (action === 'create' || action === 'update' || action === 'edit') {
            return isAdmin || isExecutive || isDirector || isSupport;
          }
          if (action === 'delete' || action === 'destroy') {
            return isAdmin || isExecutive;
          }
          if (action === 'assign' || action === 'transfer') {
            return isAdmin || isExecutive || isDirector;
          }
          if (action === 'convert') {
            return isAdmin || isExecutive || isDirector || isSupport;
          }
          return false;

        case 'Tutor':
          if (action === 'read' || action === 'index' || action === 'show') {
            return isAdmin || isExecutive || isDirector || isSupport;
          }
          if (action === 'create' || action === 'update' || action === 'edit') {
            return isAdmin || isExecutive || hasRole('tutor_director') || hasRole('tutor_support');
          }
          if (action === 'delete' || action === 'destroy') {
            return isAdmin;
          }
          if (action === 'activate' || action === 'deactivate') {
            return isAdmin || isExecutive || hasRole('tutor_director');
          }
          return false;

        case 'Student':
          if (action === 'read' || action === 'index' || action === 'show') {
            return isAdmin || isExecutive || isDirector || isSupport;
          }
          if (action === 'create' || action === 'update' || action === 'edit') {
            return isAdmin || isExecutive || isDirector || isSupport;
          }
          if (action === 'delete' || action === 'destroy') {
            return isAdmin || isExecutive;
          }
          return false;

        case 'Assignment':
        case 'DesiredPlacement':
          if (action === 'read' || action === 'index' || action === 'show') {
            return isAdmin || isExecutive || isDirector || isSupport;
          }
          if (action === 'create' || action === 'update' || action === 'edit') {
            return isAdmin || isExecutive || isDirector || isSupport;
          }
          if (action === 'delete' || action === 'destroy' || action === 'deactivate') {
            return isAdmin || isExecutive || isDirector;
          }
          return false;

        case 'TutoringSession':
        case 'OnlineSession':
          if (action === 'read' || action === 'index' || action === 'show') {
            return isAdmin || isExecutive || isDirector || isSupport;
          }
          if (action === 'create' || action === 'update' || action === 'edit') {
            return isAdmin || isExecutive || isDirector;
          }
          if (action === 'delete' || action === 'destroy') {
            return isAdmin || isExecutive;
          }
          return false;

        case 'Todo':
          // Everyone can manage their own todos
          return isAdmin || isExecutive || isDirector || isSupport;

        case 'Note':
          // Everyone can create/read notes, but only admins can delete
          if (action === 'delete' || action === 'destroy') {
            return isAdmin;
          }
          return isAdmin || isExecutive || isDirector || isSupport;

        case 'Report':
          if (action === 'read' || action === 'index' || action === 'show') {
            return isAdmin || isExecutive || isDirector;
          }
          return isAdmin;

        case 'Dashboard':
          return isAdmin || isExecutive || isDirector || isSupport;

        case 'CatalogItem':
          if (action === 'read' || action === 'index' || action === 'show') {
            return hasAnyRole(CATALOG_ROLES);
          }
          if (action === 'create' || action === 'update' || action === 'edit' || action === 'delete') {
            return hasAnyRole(CATALOG_ROLES);
          }
          return false;

        default:
          return false;
      }
    },
    [isAdmin, isExecutive, isDirector, isSupport, hasRole, hasAnyRole]
  );

  const cannot = useCallback(
    (action: PermissionAction, subject: PermissionSubject, instance?: unknown) => {
      return !can(action, subject, instance);
    },
    [can]
  );

  return (
    <PermissionContext.Provider
      value={{
        can,
        cannot,
        hasRole,
        hasAnyRole,
        isAdmin,
        isDirector,
        isSupport,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}

// Hook for checking a single permission
export function useCan(action: PermissionAction, subject: PermissionSubject) {
  const { can } = usePermissions();
  return can(action, subject);
}
